package co.maytes.standupbot.dashboard.features.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.ControlSize
import co.maytes.standupbot.dashboard.components.widgets.SegmentedControl
import co.maytes.standupbot.dashboard.components.widgets.SegmentedOption
import co.maytes.standupbot.dashboard.components.widgets.TextInput
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideChevronDown
import co.maytes.standupbot.dashboard.components.widgets.metrics
import co.maytes.standupbot.dashboard.components.widgets.CheckMark
import co.maytes.standupbot.dashboard.components.widgets.svg.Svg
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgPath
import co.maytes.standupbot.dashboard.styles.ControlClass
import co.maytes.standupbot.dashboard.styles.IconSize
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Space
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.CheckboxInput
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Button as HtmlButton

private object ReleasesPickerLayout {
    val rowHeight = Space.s9
    const val LIST_MAX_HEIGHT = "min(32rem, 70vh)"
    const val SEARCH_BASIS = "14rem"
    const val SELECTED_WEIGHT = 600
    const val MARK_PATH = "M6 12h12"
    const val MARK_STYLE = "opacity:1;stroke-dashoffset:0"
}

@Composable
internal fun ReleasesRepoPickerPanel(
    items: List<ReleasesRepoItem>,
    selected: Set<String>,
    onSelectedChange: (Set<String>) -> Unit,
) {
    var query by remember { mutableStateOf("") }
    var filter by remember { mutableStateOf(ReleasesRepoFilter.All) }
    var collapsed by remember { mutableStateOf(emptySet<String>()) }
    var limit by remember(query, filter) { mutableStateOf(ReleasesRepoPicker.PAGE_SIZE) }

    val visible = ReleasesRepoPicker.visible(items, selected, query, filter)
    val counts = ReleasesRepoPicker.counts(items, selected, query)
    val groups = ReleasesRepoPicker.groups(visible, items, selected)
    val sliced = ReleasesRepoPicker.slice(groups, collapsed, limit, forceExpanded = query.isNotBlank())

    Div(attrs = Modifier.styleModifier { stack(Spacing.sm.toString()) }.toAttrs()) {
        ReleasesPickerToolbar(query, { query = it }, filter, { filter = it }, counts)
        ReleasesPickerSummary(
            selectedCount = selected.size,
            total = items.size,
            canSelect = visible.any { it.fullName !in selected },
            canClear = visible.any { it.fullName in selected },
            onSelectAll = { onSelectedChange(ReleasesRepoPicker.selectAll(selected, visible)) },
            onClear = { onSelectedChange(ReleasesRepoPicker.clear(selected, visible)) },
        )
        ReleasesSelectedChips(
            selected = selected,
            onRemove = { onSelectedChange(selected - it) },
            onShowAll = {
                query = ""
                filter = ReleasesRepoFilter.Following
            },
        )
        if (groups.isEmpty()) {
            SettingsEmpty(
                text = ReleasesCopy.NO_MATCHES,
                action = {
                    Button(
                        label = "Clear search",
                        variant = ButtonVariant.Secondary,
                        size = ButtonSize.Sm,
                        onClick = {
                            query = ""
                            filter = ReleasesRepoFilter.All
                        },
                    )
                },
            )
        } else {
            Div(
                attrs = Modifier
                    .styleModifier {
                        property("max-height", ReleasesPickerLayout.LIST_MAX_HEIGHT)
                        property("overflow-y", "auto")
                        property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
                        property("border-radius", "${Radius.lg}")
                    }
                    .toAttrs(),
            ) {
                sliced.slices.forEachIndexed { index, slice ->
                    ReleasesGroupBlock(
                        slice = slice,
                        first = index == 0,
                        selected = selected,
                        query = query,
                        collapsible = query.isBlank(),
                        onToggleExpanded = {
                            val owner = slice.group.owner
                            collapsed = if (owner in collapsed) collapsed - owner else collapsed + owner
                        },
                        onSelectedChange = onSelectedChange,
                    )
                }
            }
            if (sliced.hidden > 0) {
                Div(attrs = Modifier.styleModifier { property("align-self", "flex-start") }.toAttrs()) {
                    Button(
                        label = ReleasesCopy.showMore(sliced.hidden),
                        variant = ButtonVariant.Secondary,
                        size = ButtonSize.Sm,
                        onClick = { limit += ReleasesRepoPicker.PAGE_SIZE },
                    )
                }
            }
        }
    }
}

@Composable
private fun ReleasesPickerToolbar(
    query: String,
    onQuery: (String) -> Unit,
    filter: ReleasesRepoFilter,
    onFilter: (ReleasesRepoFilter) -> Unit,
    counts: ReleasesFilterCounts,
) {
    Div(
        attrs = Modifier
            .styleModifier {
                property("display", "flex")
                property("flex-wrap", "wrap")
                property("align-items", "center")
                property("gap", Spacing.xs.toString())
            }
            .toAttrs(),
    ) {
        TextInput(
            value = query,
            onValueChange = onQuery,
            placeholder = "Search repositories",
            ariaLabel = "Search repositories",
            modifier = Modifier.styleModifier { property("flex", "1 1 ${ReleasesPickerLayout.SEARCH_BASIS}") },
        )
        Div(
            attrs = Modifier
                .styleModifier {
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("overflow-x", "auto")
                }
                .toAttrs(),
        ) {
            SegmentedControl(
                options = listOf(
                    SegmentedOption(ReleasesRepoFilter.All, "All", counts.all),
                    SegmentedOption(ReleasesRepoFilter.Following, "Following", counts.following),
                    SegmentedOption(ReleasesRepoFilter.NotFollowing, "Not following", counts.notFollowing),
                ),
                selected = filter,
                onSelect = onFilter,
                ariaLabel = "Filter repositories",
            )
        }
    }
}

@Composable
private fun ReleasesPickerSummary(
    selectedCount: Int,
    total: Int,
    canSelect: Boolean,
    canClear: Boolean,
    onSelectAll: () -> Unit,
    onClear: () -> Unit,
) {
    Div(
        attrs = Modifier
            .styleModifier {
                property("display", "flex")
                property("flex-wrap", "wrap")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("gap", Spacing.xs.toString())
            }
            .toAttrs { attr("aria-live", "polite") },
    ) {
        Span(
            attrs = Modifier
                .fontSize(Typography.label.size)
                .fontWeight(Typography.label.weight)
                .color(Palette.textPrimary)
                .classNames(Typography.TABULAR_CLASS)
                .toAttrs(),
        ) { Text(ReleasesCopy.selectedCount(selectedCount, total)) }
        Div(attrs = Modifier.styleModifier { property("display", "flex"); property("gap", Spacing.xxs.toString()) }.toAttrs()) {
            Button(
                label = "Select all matching",
                variant = ButtonVariant.Ghost,
                size = ButtonSize.Sm,
                enabled = canSelect,
                onClick = onSelectAll,
            )
            Button(
                label = "Clear",
                variant = ButtonVariant.Ghost,
                size = ButtonSize.Sm,
                enabled = canClear,
                onClick = onClear,
            )
        }
    }
}

@Composable
private fun ReleasesSelectedChips(selected: Set<String>, onRemove: (String) -> Unit, onShowAll: () -> Unit) {
    if (selected.isEmpty()) return
    val summary = ReleasesRepoPicker.chips(selected)
    Div(
        attrs = Modifier
            .styleModifier {
                property("display", "flex")
                property("flex-wrap", "wrap")
                property("align-items", "center")
                property("gap", Spacing.xs.toString())
            }
            .toAttrs(),
    ) {
        summary.shown.forEach { name ->
            ReleasesRemovableChip(label = name, removeLabel = "Stop following $name", onRemove = { onRemove(name) })
        }
        if (summary.more > 0) {
            Button(
                label = "+${summary.more} more",
                variant = ButtonVariant.Ghost,
                size = ButtonSize.Sm,
                onClick = onShowAll,
            )
        }
    }
}

@Composable
private fun ReleasesGroupBlock(
    slice: ReleasesGroupSlice,
    first: Boolean,
    selected: Set<String>,
    query: String,
    collapsible: Boolean,
    onToggleExpanded: () -> Unit,
    onSelectedChange: (Set<String>) -> Unit,
) {
    val group = slice.group
    val expanded = slice.expanded
    Div(
        attrs = Modifier
            .styleModifier {
                if (!first) property("border-top", "1px solid ${Palette.borderSubtle.cssRgb()}")
            }
            .toAttrs(),
    ) {
        Div(
            attrs = Modifier
                .styleModifier {
                    property("display", "flex")
                    property("align-items", "center")
                    property("min-height", "${ReleasesPickerLayout.rowHeight}")
                    property("padding-left", Spacing.xxs.toString())
                    property("background-color", Palette.surfaceSubtle.cssRgb())
                }
                .toAttrs(),
        ) {
            ReleasesTriCheck(
                state = ReleasesRepoPicker.check(group, selected),
                ariaLabel = "Follow every repository of ${group.owner}",
                onToggle = { onSelectedChange(ReleasesRepoPicker.toggleGroup(selected, group)) },
            )
            ReleasesGroupHeaderButton(group, expanded, collapsible, onToggleExpanded)
        }
        if (expanded) {
            slice.rows.forEach { item ->
                ReleasesRepoRow(
                    item = item,
                    checked = item.fullName in selected,
                    query = query,
                    onChecked = { onSelectedChange(ReleasesRepoPicker.toggleRepo(selected, item.fullName, it)) },
                )
            }
        }
    }
}

@Composable
private fun ReleasesGroupHeaderButton(group: ReleasesRepoGroup, expanded: Boolean, collapsible: Boolean, onClick: () -> Unit) {
    HtmlButton(
        attrs = Modifier
            .styleModifier {
                property("flex", "1 1 auto")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("gap", Spacing.sm.toString())
                property("min-height", "${ReleasesPickerLayout.rowHeight}")
                property("padding", "0 ${Spacing.sm}")
                property("background", "none")
                property("border", "none")
                property("font", "inherit")
                property("text-align", "left")
                property("cursor", if (collapsible) "pointer" else "default")
                property("color", Palette.textPrimary.cssRgb())
            }
            .toAttrs {
                attr("type", "button")
                attr("aria-expanded", expanded.toString())
                if (!collapsible) attr("disabled", "")
                onClick { onClick() }
            },
    ) {
        Span(attrs = Modifier.fontSize(Typography.label.size).fontWeight(Typography.label.weight).toAttrs()) { Text(group.owner) }
        Span(
            attrs = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textSecondary)
                .classNames(Typography.TABULAR_CLASS)
                .styleModifier {
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("gap", Spacing.xs.toString())
                }
                .toAttrs(),
        ) {
            Text(ReleasesCopy.groupFollowed(group.followed, group.total))
            if (collapsible) {
                Span(
                    attrs = Modifier
                        .styleModifier {
                            property("display", "inline-flex")
                            property("transition", Motion.transition(Motion.DUR_FAST, "transform"))
                            if (!expanded) property("transform", "rotate(-90deg)")
                        }
                        .toAttrs { attr("aria-hidden", "true") },
                ) { LucideChevronDown(size = IconSize.md.value.toInt()) }
            }
        }
    }
}

@Composable
private fun ReleasesRepoRow(item: ReleasesRepoItem, checked: Boolean, query: String, onChecked: (Boolean) -> Unit) {
    val (ownerParts, nameParts) = ReleasesRepoPicker.highlight(item.fullName, query)
    Label(
        attrs = Modifier
            .classNames(ControlClass.CHECK)
            .styleModifier {
                property("display", "flex")
                property("min-height", "${ReleasesPickerLayout.rowHeight}")
                property("gap", Spacing.sm.toString())
                property("padding", "0 ${Spacing.sm}")
                property("border-top", "1px solid ${Palette.borderSubtle.cssRgb()}")
                property("cursor", "pointer")
            }
            .toAttrs(),
    ) {
        CheckboxInput(
            checked = checked,
            attrs = Modifier
                .classNames(ControlClass.CHECK_INPUT)
                .toAttrs {
                    attr("aria-label", item.fullName)
                    onInput { onChecked(it.value) }
                },
        )
        Span(
            attrs = Modifier
                .classNames(ControlClass.CHECK_BOX)
                .width(ControlSize.Md.metrics.boxPx.px)
                .height(ControlSize.Md.metrics.boxPx.px)
                .toAttrs { attr("aria-hidden", "true") },
        ) { CheckMark() }
        Span(
            attrs = Modifier
                .fontSize(Typography.label.size)
                .styleModifier {
                    property("flex", "1 1 auto")
                    property("min-width", "0")
                    property("overflow-wrap", "anywhere")
                }
                .toAttrs(),
        ) {
            ReleasesHighlighted(ownerParts, dimmed = true, bold = false)
            ReleasesHighlighted(nameParts, dimmed = false, bold = checked)
        }
        item.isPrivate?.let { Badge(label = if (it) "Private" else "Public", tone = BadgeTone.Neutral) }
    }
}

@Composable
private fun ReleasesHighlighted(parts: List<ReleasesHighlightPart>, dimmed: Boolean, bold: Boolean) {
    parts.forEach { part ->
        Span(
            attrs = Modifier
                .color(if (dimmed) Palette.textTertiary else Palette.textPrimary)
                .fontWeight(if (part.match || bold) ReleasesPickerLayout.SELECTED_WEIGHT else Typography.body.weight)
                .styleModifier {
                    if (part.match) {
                        property("background-color", Palette.accentSubtle.cssRgb())
                        property("border-radius", "${Radius.inner}")
                        property("color", Palette.accentText.cssRgb())
                    }
                }
                .toAttrs(),
        ) { Text(part.text) }
    }
}

@Composable
private fun ReleasesTriCheck(state: ReleasesGroupCheck, ariaLabel: String, onToggle: () -> Unit) {
    val partial = state == ReleasesGroupCheck.Some
    Label(
        attrs = Modifier
            .classNames(ControlClass.CHECK)
            .styleModifier {
                property("justify-content", "center")
                property("min-width", "${ReleasesPickerLayout.rowHeight}")
                property("min-height", "${ReleasesPickerLayout.rowHeight}")
                property("cursor", "pointer")
            }
            .toAttrs(),
    ) {
        CheckboxInput(
            checked = state == ReleasesGroupCheck.All,
            attrs = Modifier
                .classNames(ControlClass.CHECK_INPUT)
                .toAttrs {
                    attr("aria-label", ariaLabel)
                    if (partial) attr("aria-checked", "mixed")
                    onInput { onToggle() }
                },
        )
        Span(
            attrs = Modifier
                .classNames(ControlClass.CHECK_BOX)
                .width(ControlSize.Md.metrics.boxPx.px)
                .height(ControlSize.Md.metrics.boxPx.px)
                .styleModifier {
                    if (partial) {
                        property("background-color", Palette.accent.cssRgb())
                        property("border-color", Palette.accent.cssRgb())
                    }
                }
                .toAttrs { attr("aria-hidden", "true") },
        ) {
            if (partial) {
                Svg(attrs = {
                    attr("viewBox", "0 0 24 24")
                    attr("fill", "none")
                    attr("stroke", "currentColor")
                    attr("stroke-width", "3")
                    attr("stroke-linecap", "round")
                }) {
                    SvgPath(attrs = {
                        attr("d", ReleasesPickerLayout.MARK_PATH)
                        attr("pathLength", Motion.CHECK_DASH_LENGTH.toString())
                        attr("style", ReleasesPickerLayout.MARK_STYLE)
                    })
                }
            } else {
                CheckMark()
            }
        }
    }
}

private fun StyleScope.stack(gap: String) {
    property("display", "flex")
    property("flex-direction", "column")
    property("gap", gap)
}
